import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** A horizontal proportion bar.
 *
 * Deliberately not built on `mat-progress-bar`: MDC renders the determinate fill as a full-width
 * element scaled with `transform: scaleX()`, which squashes the rounded end horizontally by the
 * same factor — badly so at low percentages, which is exactly when a "data remaining" bar matters.
 * The native `<progress>` fill is a real box sized by width, so the radius stays correct at every
 * value.
 */
@Component({
  selector: 'xemex-progress-bar',
  standalone: true,
  styleUrl: './progress-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './progress-bar.component.html',
})
export class ProgressBarComponent {
  value = input.required<number>();
  max = input.required<number>();
  /** Optional captions under the two ends of the track. Nullable because callers pass them
   * straight from the `date` pipe, which yields null. */
  startLabel = input<string | null>();
  endLabel = input<string | null>();
  ariaLabel = input<string>();

  /** `<progress>` ignores a max of zero or less and silently falls back to 1, which would draw a
   * zero-capacity bar as completely full; that case collapses to an empty track instead. The value
   * is clamped here rather than left to the browser so the element's own `value` reflects what is
   * drawn. */
  protected readonly track = computed<{ value: number; max: number }>(() => {
    const max = this.max();
    if (!Number.isFinite(max) || max <= 0) return { value: 0, max: 1 };

    const value = this.value();
    if (!Number.isFinite(value)) return { value: 0, max };
    return { value: Math.min(Math.max(value, 0), max), max };
  });

  protected readonly hasLabels = computed(() => !!this.startLabel() || !!this.endLabel());

  /** Matches the 25%/10% thresholds the SIM alarms already fire on, so the bar's colour agrees
   * with the alarm rather than tracking a separate cutoff. */
  protected readonly status = computed<'error' | 'warning' | 'normal'>(() => {
    const max = this.max();
    const value = this.value();
    if (!Number.isFinite(max) || max <= 0 || !Number.isFinite(value)) return 'normal';

    const percentRemaining = (Math.min(Math.max(value, 0), max) / max) * 100;
    if (percentRemaining <= 10) return 'error';
    if (percentRemaining <= 25) return 'warning';
    return 'normal';
  });
}
